import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Shield, LogOut, PlayCircle, Hourglass, CheckCircle2, ChevronRight, CalendarClock } from "lucide-react";

function daysLeft(archiveAt) {
  if (!archiveAt) return null;
  const diff = new Date(archiveAt).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const STATUS = {
  live: { label: "Live now", color: "var(--success)", icon: PlayCircle },
  paused: { label: "Live (paused)", color: "var(--warning)", icon: PlayCircle },
  draft: { label: "Not started", color: "var(--txt-muted)", icon: Hourglass },
  ended: { label: "Ended", color: "var(--txt-secondary)", icon: CheckCircle2 },
};

export default function SessionHub() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, []);

  const name = user?.name || (user?.email ? user.email.split("@")[0] : "there");

  return (
    <div className="min-h-screen bg-[var(--bg-base)] text-white">
      <header className="glass border-b border-white/10 flex items-center justify-between px-6 h-16">
        <button onClick={() => nav("/")} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-7 h-7 bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
          <span className="font-display font-extrabold tracking-tight">TARDIGRADE</span>
        </button>
        <button data-testid="hub-logout-btn" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-2 text-[var(--txt-muted)] hover:text-white transition-colors text-base">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 fade-up">
        <div className="overline mb-3" style={{ fontSize: "16px" }}>Welcome back</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-3">Hello, {name}.</h1>
        <p className="text-[var(--txt-secondary)] text-lg mb-12">Select a session to continue. Live and upcoming sessions are interactive; ended sessions are read-only, with feedback still open.</p>

        {sessions === null ? (
          <p className="text-[var(--txt-muted)] font-mono">Loading your sessions…</p>
        ) : sessions.length === 0 ? (
          <div className="grid-card p-10 text-center">
            <Hourglass className="w-10 h-10 mx-auto mb-4 text-[var(--txt-muted)] animate-hourglass" />
            <h2 className="font-display font-bold text-xl mb-2">No sessions assigned yet</h2>
            <p className="text-[var(--txt-secondary)]">Your facilitator hasn't assigned you to a session, or previous sessions have expired.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((s) => {
              const meta = STATUS[s.status] || STATUS.draft;
              const dl = s.status === "ended" ? daysLeft(s.archive_at) : null;
              return (
                <button key={s.id} data-testid={`hub-session-${s.id}`} onClick={() => nav(`/sim/${s.id}`)}
                  className="w-full text-left grid-card p-6 hover:bg-[var(--bg-elevated)] transition-colors group flex items-center gap-5">
                  <meta.icon className="w-8 h-8 shrink-0" style={{ color: meta.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-display font-bold text-xl truncate">{s.name}</h3>
                      <span className="px-2 py-0.5 text-sm font-mono uppercase border shrink-0" style={{ color: meta.color, borderColor: "rgba(255,255,255,0.2)" }}>{meta.label}</span>
                    </div>
                    <p className="text-base text-[var(--txt-secondary)] truncate">{s.scenario_title} · {s.scenario_type}</p>
                    {s.status === "ended" && s.archive_at && (
                      <p data-testid={`hub-available-${s.id}`} className="flex items-center gap-1.5 text-sm text-[var(--warning)] mt-2 font-mono">
                        <CalendarClock className="w-3.5 h-3.5" />
                        Available until {new Date(s.archive_at).toLocaleDateString()} · {dl} day{dl === 1 ? "" : "s"} left
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--txt-muted)] group-hover:text-white group-hover:translate-x-1 transition-all shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
